import React, { useEffect, useState } from 'react'

import { Link } from '@application/Interfaces/Atoms/Link'
import { useTranslation } from '@application/Interfaces/Hooks/useTranslation'
import { CBMComponentType } from '@application/Types/ComponentTypes'
import { translateSelectOption } from '@application/Utils/SelectOptionUtility'
import { MEASUREMENT_UNIT_OPTIONS } from '@core/Constants/FieldOptionsConstants'
import { MeasurementUnit } from '@core/Types/Measurement'
import { Grid } from '@icontainers/design-system/dist/atoms/Grid'
import { Button } from '@icontainers/design-system/dist/molecules/Button'
import { Input } from '@icontainers/design-system/dist/molecules/Input'
import { Select } from '@icontainers/design-system/dist/molecules/Select'

import {
    BoxDetails,
    TitleSection,
    VolumeLabel,
    VolumeUnit,
    VolumeValue,
    VolumeValueWrapper,
    VolumeWrapper,
} from './CBMCalculator.styled'
import { calculateCBM, handleMeasurementChange, handleMeasurementUnitChange, handleQtyChange } from './utils'

export const CBMCalculator = (props: CBMComponentType): JSX.Element => {
    const {
        title,
        subtitle,
        details,
        mode,
        cbmCalculatorTitle,
        calculatorButtonLabel,
        calculatorButtonLink,
        className,
    } = props

    const { t } = useTranslation()

    const [quantity, setQuantity] = useState('1')
    const [length, setLength] = useState('100')
    const [width, setWidth] = useState('100')
    const [height, setHeight] = useState('100')
    const [measurementUnit, setMeasurementUnit] = useState<MeasurementUnit>('cms')
    const [cbm, setCbm] = useState<number>(1)

    useEffect(() => {
        const calculatedCbm = calculateCBM({
            quantity,
            length,
            width,
            height,
            unit: measurementUnit,
        })
        setCbm(calculatedCbm)
    }, [height, length, measurementUnit, quantity, width])

    const measurementUnitOptions = translateSelectOption(MEASUREMENT_UNIT_OPTIONS, t)

    return (
        <TitleSection title={title} subtitle={subtitle} description={details} mode={mode} className={className}>
            <BoxDetails title={cbmCalculatorTitle}>
                <Grid container columnSpacing={{ xs: 2, sm: 3 }} rowSpacing={4}>
                    <Grid item xs={6} md={1.5}>
                        <Input label={t('quantity')} value={quantity} onChange={handleQtyChange(setQuantity)} />
                    </Grid>
                    <Grid item xs={6} md={1.75}>
                        <Input label={t('length')} value={length} onChange={handleMeasurementChange(setLength)} />
                    </Grid>
                    <Grid item xs={6} md={1.75}>
                        <Input label={t('width')} value={width} onChange={handleMeasurementChange(setWidth)} />
                    </Grid>
                    <Grid item xs={6} md={1.75}>
                        <Input label={t('height')} value={height} onChange={handleMeasurementChange(setHeight)} />
                    </Grid>
                    <Grid item xs={5.5} md={1.75}>
                        <Select
                            optional
                            name="unit"
                            options={measurementUnitOptions}
                            value={measurementUnitOptions.find((o) => o.value === measurementUnit)}
                            onChange={handleMeasurementUnitChange({ setMeasurementUnit })}
                        />
                    </Grid>
                    <Grid item container xs={1} md={0.5} alignItems="center" justifyContent="center">
                        =
                    </Grid>
                    <Grid item xs={5.5} md={3}>
                        <VolumeWrapper>
                            <VolumeValueWrapper>
                                <VolumeValue>{cbm}</VolumeValue>
                                <VolumeUnit>cbm</VolumeUnit>
                            </VolumeValueWrapper>
                            <VolumeLabel>{t('totalVolume')}</VolumeLabel>
                        </VolumeWrapper>
                    </Grid>
                    <Grid item xs={12}>
                        {calculatorButtonLink && (
                            <Link to={calculatorButtonLink}>
                                <Button fullWidth>{calculatorButtonLabel}</Button>
                            </Link>
                        )}
                    </Grid>
                </Grid>
            </BoxDetails>
        </TitleSection>
    )
}
